import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import SiteFooter from "@/components/SiteFooter";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 8; // up to ~16s

export default function BillingSuccessPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshAccess } = useAuth();
  const sessionId = params.get("session_id");
  const tier = params.get("tier");
  const [state, setState] = useState({ phase: "polling", message: "Confirming your payment with Stripe…" });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", message: "Missing session id." });
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshAccess();
          setState({ phase: "success", message: `Welcome to ${tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : "RankPilot"}!`, data });
          return;
        }
        if (data.status === "expired") {
          setState({ phase: "error", message: "Payment session expired. Please try again." });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ phase: "timeout", message: "Still processing — refresh your account in a minute or contact support if charged." });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        setState({ phase: "error", message: "Could not verify payment. Please contact support." });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, tier, refreshAccess]);

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100 flex flex-col items-center justify-center px-6 py-10 gap-6" data-testid="billing-success-page">
      <div className="max-w-md w-full border border-zinc-800 rounded-sm bg-zinc-950 p-8 text-center space-y-6">
        {state.phase === "polling" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-sm bg-[#0055FF]/15 border border-[#0055FF]/40 grid place-items-center">
              <Loader2 className="w-7 h-7 text-[#3377FF] animate-spin" />
            </div>
            <h1 className="font-display text-2xl font-semibold tracking-tight">Almost there…</h1>
            <p className="text-sm text-zinc-400 leading-relaxed">{state.message}</p>
          </>
        )}
        {state.phase === "success" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-sm bg-[#10B981]/15 border border-[#10B981]/40 grid place-items-center">
              <CheckCircle2 className="w-8 h-8 text-[#10B981]" />
            </div>
            <h1 className="font-display text-2xl font-semibold tracking-tight">{state.message}</h1>
            <p className="text-sm text-zinc-400 leading-relaxed">All features are unlocked. Time to ship some rankings.</p>
            <Button onClick={() => navigate("/dashboard")} className="w-full bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11" data-testid="billing-success-cta">
              Go to dashboard <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </>
        )}
        {state.phase === "timeout" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-sm bg-amber-500/15 border border-amber-500/40 grid place-items-center">
              <Loader2 className="w-7 h-7 text-amber-400" />
            </div>
            <h1 className="font-display text-2xl font-semibold tracking-tight">Still processing…</h1>
            <p className="text-sm text-zinc-400 leading-relaxed">{state.message}</p>
            <Button onClick={() => navigate("/account")} variant="outline" className="w-full bg-zinc-950 border-zinc-800 rounded-sm h-11">View account</Button>
          </>
        )}
        {state.phase === "error" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-sm bg-red-500/15 border border-red-500/40 grid place-items-center">
              <XCircle className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="font-display text-2xl font-semibold tracking-tight">Something went wrong</h1>
            <p className="text-sm text-zinc-400 leading-relaxed">{state.message}</p>
            <div className="flex gap-3">
              <Button onClick={() => navigate("/upgrade")} className="flex-1 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm h-11">Try again</Button>
              <Button asChild variant="outline" className="flex-1 bg-zinc-950 border-zinc-800 rounded-sm h-11">
                <Link to="/account">Account</Link>
              </Button>
            </div>
          </>
        )}
      </div>
      <SiteFooter variant="minimal" />
    </div>
  );
}
